"use client";

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Search } from "@/components/ui/icons";
import { cn } from "@/lib/utils";

const EASE = [0.23, 1, 0.32, 1] as const;
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** Gap between trigger and panel, and the breathing room kept against the viewport edge. */
const GAP = 8;
const EDGE = 16;
/** The navbar is fixed and paints above the page, so a panel opening upward stops short of it. */
const NAV_EDGE = 68;
/** Below this the list is too short to be worth opening downward. */
const MIN_LIST = 160;
const MAX_LIST = 264;

export type SelectOption = {
  value: string;
  /** What the trigger and the row show. */
  label: string;
  /** Leading visual, e.g. a flag. */
  icon?: ReactNode;
  /** Muted text trailing the label in the list, e.g. "coming soon". */
  note?: string;
  /** Extra words the search should match on but that are not displayed. */
  keywords?: string;
};

export type SelectGroup = { label?: string; options: SelectOption[] };

type SelectProps = {
  /** Posted with the form — the control keeps a hidden input so FormData still works. */
  name: string;
  value: string;
  onChange: (value: string) => void;
  groups: SelectGroup[];
  placeholder?: string;
  /** Adds a filter box once the list is long enough to need one. */
  searchable?: boolean;
  searchPlaceholder?: string;
  id?: string;
  ariaLabel?: string;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  invalid?: boolean;
  className?: string;
  /** Widens or narrows the panel independently of the trigger. */
  panelClassName?: string;
  /** Renders the trigger's selected content — defaults to the option's icon + label. */
  renderValue?: (option: SelectOption) => ReactNode;
};

/**
 * A listbox in the page's own idiom, replacing the native `<select>`: the OS menu cannot be
 * styled, cannot show a flag, and looked nothing like the rest of the form. Keeps a hidden
 * input so it still posts like a normal field.
 */
export function Select({
  name,
  value,
  onChange,
  groups,
  placeholder = "Select…",
  searchable = false,
  searchPlaceholder = "Search…",
  id,
  ariaLabel,
  ariaLabelledby,
  ariaDescribedby,
  invalid,
  className,
  panelClassName,
  renderValue,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<"bottom" | "top">("bottom");
  const [listMax, setListMax] = useState(MAX_LIST);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const uid = useId();
  const listId = `${uid}-list`;

  const all = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  const selected = all.find((o) => o.value === value);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        options: g.options.filter((o) => `${o.label} ${o.keywords ?? ""}`.toLowerCase().includes(q)),
      }))
      .filter((g) => g.options.length);
  }, [groups, query]);

  const flat = useMemo(() => shown.flatMap((g) => g.options), [shown]);

  // Open on the current choice so the list starts where the user left it.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    const i = flat.findIndex((o) => o.value === value);
    setActive(i < 0 ? 0 : i);
    const t = window.setTimeout(() => {
      if (searchable) searchRef.current?.focus();
      listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "center" });
    }, 20);
    return () => window.clearTimeout(t);
    // `flat` is intentionally read once per opening, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Open downward by default, but flip above the trigger when the list would otherwise run
   * off the bottom of the window, and cap its height to whatever room is actually there.
   */
  useIsoLayoutEffect(() => {
    if (!open) return;
    const fit = () => {
      const t = trigger.current;
      const panel = panelRef.current;
      const list = listRef.current;
      if (!t || !panel || !list) return;

      const r = t.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - GAP - EDGE;
      const above = r.top - GAP - NAV_EDGE;
      const next = below < MIN_LIST && above > below ? "top" : "bottom";
      setPlacement(next);

      // Everything in the panel that is not the scrolling list: padding and the search box.
      const chrome = panel.offsetHeight - list.offsetHeight;
      const room = (next === "top" ? above : below) - chrome;
      setListMax(Math.max(120, Math.min(MAX_LIST, room)));
    };

    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("scroll", fit, true);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("scroll", fit, true);
    };
  }, [open, shown]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    trigger.current?.focus();
  };

  const move = (delta: number) => {
    if (!flat.length) return;
    const next = (active + delta + flat.length) % flat.length;
    setActive(next);
    listRef.current
      ?.querySelectorAll("[data-opt]")
      [next]?.scrollIntoView({ block: "nearest" });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(flat.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flat[active]) pick(flat[active].value);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  let index = -1;

  return (
    <div ref={root} className={cn("relative", className)} onKeyDown={onKeyDown}>
      <input type="hidden" name={name} value={value} />

      <button
        ref={trigger}
        id={id}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        // `aria-invalid` is not valid on a button; the red ring shows it, and the error text
        // reaches assistive tech through `aria-describedby`.
        aria-describedby={ariaDescribedby}
        className={cn(
          "flex h-11 w-full items-center justify-between gap-2 rounded-[12px] bg-white px-3.5 text-left text-[14px] outline-none ring-1 ring-inset transition-shadow",
          "focus-visible:ring-2 focus-visible:ring-accent",
          invalid ? "ring-2 ring-[#c2413b]" : "ring-ink/[0.08]",
        )}
      >
        <span className={cn("flex min-w-0 items-center gap-2", !selected && "text-faint")}>
          {selected ? (renderValue ? renderValue(selected) : <><>{selected.icon}</><span className="truncate">{selected.label}</span></>) : placeholder}
        </span>
        <ChevronDown className={cn("size-3 shrink-0 text-muted transition-transform duration-300 ease-[var(--ease-premium)]", open && "rotate-180")} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            tabIndex={-1}
            // Lenis drives the page from the wheel, so an inner scroller has to opt out of it.
            data-lenis-prevent=""
            initial={{ opacity: 0, y: placement === "top" ? 6 : -6, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: placement === "top" ? 6 : -6, filter: "blur(4px)" }}
            transition={{ duration: 0.22, ease: EASE }}
            className={cn(
              "absolute left-0 z-40 w-full min-w-[200px] overflow-hidden rounded-[18px] bg-white p-1.5 shadow-[0_18px_48px_rgba(10,10,11,0.12)] ring-1 ring-inset ring-ink/[0.07]",
              placement === "top" ? "bottom-[calc(100%+8px)]" : "top-[calc(100%+8px)]",
              panelClassName,
            )}
          >
            {searchable && (
              <div className="relative mb-1 px-1 pt-1">
                <Search aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  className="h-9 w-full rounded-[10px] bg-panel pl-8 pr-3 text-[13px] outline-none ring-1 ring-inset ring-transparent transition-shadow placeholder:text-faint focus:ring-2 focus:ring-accent"
                />
              </div>
            )}

            <div ref={listRef} style={{ maxHeight: listMax }} className="no-scrollbar overflow-y-auto overscroll-contain">
              {flat.length === 0 && <p className="px-3 py-4 text-center text-[13px] text-faint">No matches.</p>}

              {shown.map((g, gi) => (
                <div key={g.label ?? gi}>
                  {g.label && (
                    <p className="px-3 pb-1 pt-2.5 text-[10.5px] font-medium uppercase tracking-[0.09em] text-faint">{g.label}</p>
                  )}
                  {g.options.map((o) => {
                    index += 1;
                    const i = index;
                    const on = o.value === value;
                    return (
                      <button
                        key={o.value}
                        data-opt=""
                        data-active={i === active}
                        type="button"
                        role="option"
                        aria-selected={on}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => pick(o.value)}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-[12px] px-3 py-2 text-left text-[14px] transition-colors",
                          i === active && "bg-panel",
                          on && "font-medium",
                        )}
                      >
                        {o.icon}
                        <span className="min-w-0 flex-1 truncate">
                          {o.label}
                          {o.note && <span className="ml-1.5 text-[12px] text-faint">{o.note}</span>}
                        </span>
                        {on && <Check className="size-3.5 shrink-0 text-accent" strokeWidth={2.4} />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
